<script setup>
import { computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Carga por Hora do Dia',
  },
});

const hourlyData = computed(() => {
  if (!props.data || props.data.length === 0) {
    // Generate empty 24-hour data
    return Array.from({ length: 24 }, (_, i) => ({
      hour: i,
      count: 0,
    }));
  }
  return props.data;
});

const maxCount = computed(() => {
  return Math.max(...hourlyData.value.map(h => h.count), 1);
});

const totalConversations = computed(() => {
  return hourlyData.value.reduce((sum, h) => sum + h.count, 0);
});

const peakHour = computed(() => {
  if (totalConversations.value === 0) return null;
  return hourlyData.value.reduce(
    (max, h) => (h.count > max.count ? h : max),
    hourlyData.value[0]
  );
});

function formatHour(hour) {
  return `${hour.toString().padStart(2, '0')}:00`;
}

function getBarHeight(count) {
  return `${(count / maxCount.value) * 100}%`;
}

function getBarColor(hour) {
  // Business hours coloring
  if (hour >= 9 && hour < 18) return 'bg-n-blue-9';
  if (hour >= 18 && hour < 22) return 'bg-n-amber-9';
  return 'bg-n-slate-7';
}
</script>

<template>
  <div class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-2">
        <span class="i-lucide-clock text-lg text-n-blue-10" />
        <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
      </div>
      <div v-if="peakHour" class="flex items-center gap-2 text-xs">
        <span class="text-n-slate-11">Pico:</span>
        <span class="font-medium text-n-blue-11">
          {{ formatHour(peakHour.hour) }} ({{ peakHour.count }} conv.)
        </span>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-48">
      <Spinner size="large" />
    </div>

    <!-- Chart -->
    <div v-else class="flex flex-col">
      <!-- Bars -->
      <div class="flex items-end gap-0.5 h-32">
        <div
          v-for="hour in hourlyData"
          :key="hour.hour"
          class="flex-1 flex flex-col items-center"
        >
          <div class="w-full bg-n-alpha-1 rounded-t h-full flex items-end">
            <div
              class="w-full rounded-t transition-all duration-300"
              :class="getBarColor(hour.hour)"
              :style="{ height: getBarHeight(hour.count) }"
            />
          </div>
        </div>
      </div>

      <!-- Hour labels (show every 3 hours) -->
      <div class="flex gap-0.5 mt-1 border-t border-n-weak pt-1">
        <div
          v-for="hour in hourlyData"
          :key="`label-${hour.hour}`"
          class="flex-1 text-center"
        >
          <span v-if="hour.hour % 3 === 0" class="text-[10px] text-n-slate-10">
            {{ hour.hour }}h
          </span>
        </div>
      </div>

      <!-- Legend and summary -->
      <div
        class="flex items-center justify-between mt-4 pt-3 border-t border-n-weak"
      >
        <div class="flex items-center gap-4 text-xs">
          <div class="flex items-center gap-1">
            <span class="w-2 h-2 rounded bg-n-blue-9" />
            <span class="text-n-slate-11">Comercial (9-18h)</span>
          </div>
          <div class="flex items-center gap-1">
            <span class="w-2 h-2 rounded bg-n-amber-9" />
            <span class="text-n-slate-11">Noite (18-22h)</span>
          </div>
          <div class="flex items-center gap-1">
            <span class="w-2 h-2 rounded bg-n-slate-7" />
            <span class="text-n-slate-11">Madrugada</span>
          </div>
        </div>
        <span class="text-sm font-medium text-n-slate-12">
          {{ totalConversations }} total
        </span>
      </div>
    </div>
  </div>
</template>
